import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PersonAvatar } from '../shared';
import { useCreateTeam } from '@/hooks/queries/useTeams';
import { useVolunteers } from '@/hooks/queries';

interface CreateTeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateTeamDialog({ open, onOpenChange }: CreateTeamDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [leadSearch, setLeadSearch] = useState('');
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);

  const createTeam = useCreateTeam();
  const { data: volunteers = [] } = useVolunteers();

  const selectedLead = selectedLeadId
    ? volunteers.find((v) => v.userId === selectedLeadId)
    : null;

  const filteredLeads = leadSearch.trim()
    ? volunteers
        .filter(
          (v) =>
            v.firstName.toLowerCase().includes(leadSearch.toLowerCase()) ||
            v.lastName.toLowerCase().includes(leadSearch.toLowerCase()) ||
            v.email.toLowerCase().includes(leadSearch.toLowerCase())
        )
        .slice(0, 8)
    : volunteers.slice(0, 8);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    createTeam.mutate(
      {
        name: name.trim(),
        description: description.trim() || null,
        leadUserId: selectedLeadId || null,
        memberIds: null,
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setLeadSearch('');
    setSelectedLeadId(null);
    setShowLeadDropdown(false);
    onOpenChange(false);
  };

  const handleSelectLead = (volunteerId: string) => {
    setSelectedLeadId(volunteerId);
    setLeadSearch('');
    setShowLeadDropdown(false);
  };

  const handleClearLead = () => {
    setSelectedLeadId(null);
    setLeadSearch('');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create Team</DialogTitle>
            <DialogDescription>
              Create a new team to organize your campaign staff.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Team Name */}
            <div className="space-y-2">
              <label htmlFor="team-name" className="text-sm font-medium">
                Team Name <span className="text-destructive">*</span>
              </label>
              <Input
                id="team-name"
                placeholder="e.g., Downtown Canvassers"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label htmlFor="team-desc" className="text-sm font-medium">
                Description
              </label>
              <Input
                id="team-desc"
                placeholder="Brief description of this team's purpose"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {/* Team Lead */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Team Lead</label>
              {selectedLead ? (
                <div className="flex items-center gap-2 p-2 border rounded-md">
                  <PersonAvatar
                    firstName={selectedLead.firstName}
                    lastName={selectedLead.lastName}
                    size="sm"
                  />
                  <span className="text-sm flex-1 truncate">
                    {selectedLead.firstName} {selectedLead.lastName}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={handleClearLead}
                  >
                    Clear
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <Input
                    placeholder="Search for a team lead..."
                    value={leadSearch}
                    onChange={(e) => {
                      setLeadSearch(e.target.value);
                      setShowLeadDropdown(true);
                    }}
                    onFocus={() => setShowLeadDropdown(true)}
                  />
                  {showLeadDropdown && filteredLeads.length > 0 && (
                    <div className="absolute z-10 top-full mt-1 w-full border rounded-md bg-popover shadow-md max-h-[200px] overflow-y-auto">
                      {filteredLeads.map((v) => (
                        <button
                          key={v.userId}
                          type="button"
                          className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-accent text-sm"
                          onClick={() => handleSelectLead(v.userId)}
                        >
                          <PersonAvatar
                            firstName={v.firstName}
                            lastName={v.lastName}
                            size="sm"
                          />
                          <div className="min-w-0">
                            <span className="truncate block">
                              {v.firstName} {v.lastName}
                            </span>
                            <span className="truncate block text-xs text-muted-foreground">
                              {v.email}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || createTeam.isPending}>
              {createTeam.isPending ? 'Creating...' : 'Create Team'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
